import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus, faDownload } from '@fortawesome/free-solid-svg-icons';
import EventCard from './EventCard';
import CreateEventModal from './CreateEventModal';
import groupService from '../../api/groupService';
import calendarApi from '../../services/calendarApi';
import { AdminPageLayout, AdminPageFilters } from '../common';
import '../../styles/group/EventsList.css';
import '../../styles/FinancialsPage.css'; // For chip filter styles

const EventsList = ({ groupId, searchQuery, refreshTrigger, isAdmin }) => {
  const [events, setEvents] = useState([]);
  const [eventCategories, setEventCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApiError, setHasApiError] = useState(false); // Track if we had API errors
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all'); // Active category filter
  const [sortBy, setSortBy] = useState('newest');
  const [activeCalendar, setActiveCalendar] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });

  // Auto-hide snackbar after 3 seconds
  useEffect(() => {
    if (snackbar.show) {
      const timer = setTimeout(() => {
        setSnackbar({ ...snackbar, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.show]);
  
  // Fetch default calendar for this group
  useEffect(() => {
    const fetchDefaultCalendar = async () => {
      if (!groupId) return;
      
      try {
        const response = await calendarApi.getCalendars(groupId);
        const calendarsData = response.data || [];
        const defaultCalendar = calendarsData.find(c => c.is_default) || calendarsData[0];
        setActiveCalendar(defaultCalendar?.id || null);
      } catch (err) {
        console.error('Error fetching calendars:', err);
      }
    };

    fetchDefaultCalendar();
  }, [groupId]);

  // Fetch events and categories for this group
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setHasApiError(false); // Reset API error flag
      
      // Fetch both events and categories in parallel
      const [eventsResponse, categoriesResponse] = await Promise.all([
        groupService.getGroupEvents(groupId),
        groupService.getEventCategories(groupId)
      ]);
      
      setEvents(eventsResponse.data);
      setEventCategories(categoriesResponse.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      setHasApiError(true); // Set API error flag
      setError('Please refresh the page to reload events');
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents, refreshTrigger]);

  // Handle edit event
  const handleEditEvent = (event) => {
    setCurrentEvent(event);
    setShowEditModal(true);
  };

  // Handle event update
  const handleEventUpdated = (updatedEvent) => {
    // Update the event in the list
    setEvents(events.map(event => 
      event.id === updatedEvent.id ? updatedEvent : event
    ));
    
    // If the updated event has a category that's not in our list, add it
    if (updatedEvent.category && !eventCategories.includes(updatedEvent.category)) {
      setEventCategories([...eventCategories, updatedEvent.category]);
    }
    
    setShowEditModal(false);
    setCurrentEvent(null);
    
    // Show success snackbar
    setSnackbar({ show: true, message: 'Event updated successfully!', type: 'success' });
  };

  // Handle delete event
  const handleDeleteEvent = async (eventId) => {
    try {
      await groupService.deleteEvent(groupId, eventId);
      setEvents(events.filter(event => event.id !== eventId));
      console.log('Event deleted successfully');
      
      // Show success snackbar
      setSnackbar({ show: true, message: 'Event deleted successfully!', type: 'success' });
    } catch (error) {
      console.error('Error deleting event:', error);
      
      // Show error snackbar
      setSnackbar({ show: true, message: 'Failed to delete event. Please try again.', type: 'error' });
    }
  };

  // Handle create new event
  const handleCreateNew = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  // Prepare filter components
  const renderFilters = useCallback(() => {
    if (!isAdmin) return null;
    
    // Get unique categories with counts
    const uniqueCategories = eventCategories.map(category => ({
      value: category,
      label: category,
      count: events.filter(e => e.category === category).length
    }));
    
    return (
      <>
        <div className="chip-filters-container">
          <div className="chip-filters">
            <button
              className={`chip-filter ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              All
            </button>
            {uniqueCategories.map(category => (
              <button
                key={category.value}
                className={`chip-filter ${activeCategory === category.value ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.value)}
              >
                <span>{category.label}</span>
              </button>
            ))}
          </div>
          
          {/* Right side controls - Sort and New Event button */}
          <div className="right-controls">
            <AdminPageFilters.SortDropdown
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: 'newest', label: 'Newest' },
                { value: 'oldest', label: 'Oldest' },
                { value: 'name', label: 'Name' }
              ]}
            />
            
            <button 
              className="btn btn-primary"
              data-tour="new-event-button"
              onClick={handleCreateNew}
              style={{
                backgroundColor: '#5669FF',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap'
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              New Event
            </button>
          </div>
        </div>
      </>
    );
  }, [isAdmin, activeCategory, eventCategories, events, sortBy, handleCreateNew]);

  // Handle event created
  const handleEventCreated = (newEvent) => {
    setEvents([newEvent, ...events]);
    
    // If the new event has a category that's not in our list, add it
    if (newEvent.category && !eventCategories.includes(newEvent.category)) {
      setEventCategories([...eventCategories, newEvent.category]);
    }
    
    setShowCreateModal(false);
    
    // Show success snackbar
    setSnackbar({ show: true, message: 'Event created successfully!', type: 'success' });
  };

  // Filter events based on active category
  let filteredEvents = events;

  // Apply category filter
  if (activeCategory && activeCategory !== 'all') {
    filteredEvents = filteredEvents.filter(event => event.category === activeCategory);
  }

  // Apply sorting
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.date) - new Date(b.date);
      case 'name':
        return a.title.localeCompare(b.title);
      case 'newest':
      default:
        return new Date(b.date) - new Date(a.date);
    }
  });

  // Handle page refresh
  const handleRefreshPage = () => {
    window.location.reload();
  };

  if (loading) {
    if (isAdmin) {
      return (
        <AdminPageLayout
          title="Events"
          icon={<FontAwesomeIcon icon={faCalendarAlt} />}
          subtitle="Loading events..."
          loading={true}
        />
      );
    } else {
      return (
        <div className="member-events-container">
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading events...</div>
        </div>
      );
    }
  }

  if (error) {
    if (isAdmin) {
      return (
        <AdminPageLayout
          title="Events"
          icon={<FontAwesomeIcon icon={faCalendarAlt} />}
          subtitle="Error loading events"
          error={error}
        />
      );
    } else {
      return (
        <div className="member-events-container">
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
            Error loading events. Please try again later.
          </div>
        </div>
      );
    }
  }

  // Render empty state content
  const renderEmptyState = () => {
    // Show error state if we detected API errors
    if (hasApiError || error) {
      if (isAdmin) {
        return (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FontAwesomeIcon icon={faCalendarAlt} size="4x" />
            </div>
            <h2>Something went wrong...</h2>
            <p>Unable to load events</p>
            <button 
              className="try-again-button"
              onClick={handleRefreshPage}
            >
              Try Again
            </button>
          </div>
        );
      } else {
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Unable to load events. Please try again later.</p>
          </div>
        );
      }
    }
    
    // Show different empty state for admin vs member
    if (isAdmin) {
      return (
        <div className="events-empty-state">
          <span className="empty-state-emoji">📅</span>
          <h3>No Events Yet</h3>
          <p>Create your first event in the Yesod app to share with your community.</p>
          <p>Only approved members who have access to your community will see your events.</p>
          <p className="empty-state-action-hint">Tap the <strong>"New"</strong> button above to get started.</p>
        </div>
      );
    } else {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>No events yet.</p>
        </div>
      );
    }
  };

  const headerStats = (
    <div className="stats-grid">
      <div className="stat-card">
        <div className="stat-icon bg-blue-100">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600" />
        </div>
        <div className="stat-content">
          <div className="stat-label">Total Events</div>
          <div className="stat-number">{Array.isArray(events) ? events.length : 0}</div>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon bg-green-100">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-green-600" />
        </div>
        <div className="stat-content">
          <div className="stat-label">With Category</div>
          <div className="stat-number">{Array.isArray(events) ? events.filter(event => event.category).length : 0}</div>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon bg-gray-100">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-600" />
        </div>
        <div className="stat-content">
          <div className="stat-label">Categories</div>
          <div className="stat-number">{eventCategories.length}</div>
        </div>
      </div>
    </div>
  );

  const headerActions = null;

  // Conditional wrapper component for member vs admin view
  const LayoutWrapper = ({ children }) => {
    if (isAdmin) {
      return (
        <AdminPageLayout
          className="community-feed-seamless"
          title="Events"
          icon={<FontAwesomeIcon icon={faCalendarAlt} />}
          subtitle="Add and manage your community's events here. All events added will appear in the app for members to view."
          headerActions={headerActions}
          stats={headerStats}
          showFilters={isAdmin}
          filters={renderFilters()}
          loading={false}
          error={null}
          emptyState={sortedEvents.length === 0 ? renderEmptyState() : null}
        >
          {children}
        </AdminPageLayout>
      );
    } else {
      // Member view - just show content without dashboard headers
      return (
        <div className="member-events-container">
          {sortedEvents.length === 0 ? renderEmptyState() : children}
        </div>
      );
    }
  };

  return (
    <LayoutWrapper>
      {sortedEvents.length > 0 ? (
        <div className="events-list">
          {sortedEvents.map(event => (
            <EventCard 
              key={event.id}
              event={event} 
              groupId={groupId}
              isAdmin={isAdmin}
              onEditEvent={handleEditEvent}
              onDeleteEvent={handleDeleteEvent}
            />
          ))}
        </div>
      ) : (
        renderEmptyState()
      )}
      
      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          groupId={groupId}
          onEventCreated={handleEventCreated}
          activeCalendar={activeCalendar}
          hideCalendarSelector={true}
        />
      )}
      
      {/* Edit Event Modal */}
      {showEditModal && (
        <CreateEventModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setCurrentEvent(null);
          }}
          groupId={groupId}
          onEventUpdated={handleEventUpdated}
          event={currentEvent}
          isEditMode={true}
          activeCalendar={activeCalendar}
          hideCalendarSelector={true}
        />
      )}
      
      {/* Snackbar Notification */}
      {snackbar.show && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
          <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
            snackbar.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          } text-white`}>
            {snackbar.type === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="font-medium">{snackbar.message}</span>
          </div>
        </div>
      )}
    </LayoutWrapper>
  );
};

export default EventsList;
